import { useEffect, useState } from "react";
import { AlertTriangle, ChefHat, ClipboardList, Package, Printer } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function PrepSheet() {
  const [day, setDay] = useState(new Date().toISOString().split("T")[0]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (d) => {
    setLoading(true);
    try {
      const { data } = await api.get("/prep-sheet", { params: { day: d } });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(day); }, []);

  const change = (v) => { setDay(v); load(v); };

  return (
    <div data-testid="prep-sheet-page" className="space-y-6">
      <div className="flex items-center justify-between print:hidden">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Prep Sheet</h1>
          <p className="mt-1 text-sm text-muted-foreground">Everything the kitchen needs for one day — bookings, buffet orders, allergens, equipment.</p>
        </div>
        <div className="flex items-center gap-2">
          <Input data-testid="prep-date-input" type="date" value={day} onChange={(e) => change(e.target.value)} className="w-44" />
          <Button data-testid="prep-print-button" variant="outline" className="rounded-md" onClick={() => window.print()}><Printer className="mr-2 h-4 w-4" /> Print</Button>
        </div>
      </div>

      {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {data && !loading && (
        <div className="space-y-6">
          <div className="border border-border bg-white p-6" data-testid="prep-events">
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground"><ClipboardList className="h-4 w-4" /> Events ({data.bookings.length})</p>
            {data.bookings.length === 0 ? <p className="mt-3 text-sm text-muted-foreground">No confirmed events this day.</p> : (
              <div className="mt-3 space-y-2">
                {data.bookings.map((b, i) => (
                  <div key={i} className="flex items-center justify-between border-b border-border pb-2 text-sm last:border-0" data-testid={`prep-event-${i}`}>
                    <span className="font-semibold">{b.ref} — {b.customer}</span>
                    <span className="text-muted-foreground">{b.guests} guests · {b.venue || "no venue"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border border-border bg-white p-6" data-testid="prep-dishes">
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground"><ChefHat className="h-4 w-4" /> Dishes to prep</p>
            {data.dishes.length === 0 ? <p className="mt-3 text-sm text-muted-foreground">Nothing to prep.</p> : (
              <div className="overflow-x-auto">
              <table className="mt-3 w-full text-sm">
                <thead><tr className="border-b border-border text-left"><th className="py-2 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Dish</th><th className="py-2 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Course</th><th className="py-2 text-right text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Portions</th><th className="py-2 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Events</th></tr></thead>
                <tbody>
                  {data.dishes.map((d, i) => (
                    <tr key={i} className="border-b border-border last:border-0" data-testid={`prep-dish-${i}`}>
                      <td className="py-2 font-medium">{d.name}</td>
                      <td className="py-2 capitalize text-muted-foreground">{d.course}</td>
                      <td className="py-2 text-right font-heading text-base font-black tabular-nums">{d.portions}</td>
                      <td className="py-2 text-xs text-muted-foreground">{d.events.join(", ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
            {data.order_totals.length > 0 && (
              <div className="mt-4 border-t border-border pt-3" data-testid="prep-order-lines">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Buffet order lines</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {data.order_totals.map((l, i) => <span key={i} className="border border-border bg-muted px-2.5 py-1 text-xs font-semibold">{l.qty}× {l.name}</span>)}
                </div>
              </div>
            )}
          </div>

          {data.allergens.length > 0 && (
            <div className="border border-amber-300 bg-amber-50 p-6" data-testid="prep-allergens">
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-amber-800"><AlertTriangle className="h-4 w-4" /> Allergens present today</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {data.allergens.map((a) => <span key={a} className="border border-amber-300 bg-white px-2.5 py-1 text-xs font-bold capitalize text-amber-900">{a}</span>)}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="border border-border bg-white p-6" data-testid="prep-equipment">
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground"><Package className="h-4 w-4" /> Equipment to load</p>
              {data.equipment.length === 0 ? <p className="mt-3 text-sm text-muted-foreground">No equipment assigned.</p> : (
                <div className="mt-3 space-y-1.5">
                  {data.equipment.map((e, i) => (
                    <div key={i} className="flex justify-between text-sm" data-testid={`prep-equipment-${i}`}><span className="font-medium">{e.name}</span><span className="font-bold tabular-nums">×{e.qty}</span></div>
                  ))}
                </div>
              )}
            </div>
            <div className="border border-border bg-white p-6" data-testid="prep-buffet-orders">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Buffet collections ({data.buffet_orders.length})</p>
              {data.buffet_orders.length === 0 ? <p className="mt-3 text-sm text-muted-foreground">No buffet pickups this day.</p> : (
                <div className="mt-3 space-y-2">
                  {data.buffet_orders.map((o, i) => (
                    <div key={i} className="border-b border-border pb-2 text-sm last:border-0" data-testid={`prep-buffet-${i}`}>
                      <span className="font-semibold">{o.time || "—"} — {o.name}</span>
                      <span className="ml-2 text-xs text-muted-foreground">{o.items.map((l) => `${l.qty}× ${l.name}`).join(", ")}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
